/**
 * ReplayContainer Component
 *
 * Main orchestration component for the writing replay UI.
 * Coordinates all sub-components and manages the two-level hierarchy:
 * - Top level: Navigate through key moments (batched edits)
 * - Drilled down: Navigate through individual edits within a moment
 *
 * Uses ProseMirror-based rich text replay for full formatting fidelity.
 *
 * Layout:
 * ┌──────────────────────────────────────────────────────────────────┐
 * │ [ReplayBreadcrumb - only when drilled down]                      │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ [ReplayProgressBar]                                              │
 * │ Moment 5 of 28                                                   │
 * ├──────────────────────────────────────────────────────────────────┤
 * │                                                                  │
 * │                    [ReplayViewProseMirror]                       │
 * │                    Full document with highlights                 │
 * │                                                                  │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ [ReplayMomentCard]                                               │
 * │ PASTED 147 WORDS - Monday 2:34 PM - Instant                     │
 * │ 3 edits [See edits]                                              │
 * ├──────────────────────────────────────────────────────────────────┤
 * │ [ReplayControls]                                                 │
 * │ [Previous]  [Play]  [Next]   Speed: [1x]                        │
 * └──────────────────────────────────────────────────────────────────┘
 *
 * @example
 * ```tsx
 * <ReplayContainer
 *   events={editEvents}
 *   initialText={document.chunkedSnapshot}
 * />
 * ```
 */

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import type { ExtensionEditEvent } from "@markeroo/shared";
import { schema } from "@markeroo/editor";
import { batchEdits } from "../core/batchEdits";
import {
  buildPMDocumentState,
  createInitialDocument,
} from "../core/buildPMDocument";
import { ReplayProgressBar } from "./ReplayProgressBar";
import { ReplayMomentCard } from "./ReplayMomentCard";
import { ReplayViewProseMirror } from "./ReplayViewProseMirror";
import {
  ReplayControls,
  SPEED_CONFIG,
  type PlaybackSpeed,
} from "./ReplayControls";
import { ReplayBreadcrumb } from "./ReplayBreadcrumb";

/**
 * Strings for the ReplayContainer component.
 * These can be overridden via props for i18n.
 */
export interface ReplayContainerStrings {
  highlightPosition: (current: number, total: number) => string;
  momentProgress: (current: number, total: number) => string;
  noEventsAvailable: string;
  noContentYet: string;
}

const defaultStrings: ReplayContainerStrings = {
  highlightPosition: (current, total) => `Edit ${current} of ${total}`,
  momentProgress: (current, total) => `Moment ${current} of ${total}`,
  noEventsAvailable: "No edit events available for replay",
  noContentYet: "No content yet",
};

/**
 * Navigation state for URL deep-linking.
 * Values are 1-indexed to match user-facing display.
 */
export interface ReplayNavigationState {
  /** Current moment index (1-indexed) */
  moment: number;
  /** Current edit index within moment, null if not drilled down (1-indexed) */
  edit: number | null;
}

export interface ReplayContainerProps {
  /** Raw edit events from API */
  events: ExtensionEditEvent[];
  /** Initial document text (from chunkedSnapshot) */
  initialText?: string | null;
  /** Optional className */
  className?: string;
  /** Override strings for i18n */
  strings?: Partial<ReplayContainerStrings>;
  /** Custom Select component for ReplayControls */
  SelectComponent?: React.ComponentType<{
    id: string;
    value: number;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    className?: string;
    "aria-label"?: string;
    "data-testid"?: string;
    children: React.ReactNode;
  }>;
  /**
   * Initial navigation state for deep-linking (values are 1-indexed).
   * If provided, replay will start at the specified position.
   * Invalid values are clamped to valid ranges.
   */
  initialNavigationState?: Partial<ReplayNavigationState>;
  /**
   * Callback when navigation state changes (for URL sync).
   * Called on manual navigation and when auto-play pauses.
   * Values are 1-indexed to match user-facing display.
   */
  onNavigationChange?: (state: ReplayNavigationState) => void;
}

export function ReplayContainer({
  events,
  initialText,
  className = "",
  strings: stringOverrides,
  SelectComponent,
  initialNavigationState,
  onNavigationChange,
}: ReplayContainerProps) {
  const strings = { ...defaultStrings, ...stringOverrides };

  // Batch events into key moments (memoized)
  const keyMoments = useMemo(() => batchEdits(events), [events]);

  // Build ProseMirror document state manager (rich text replay)
  const documentBuilder = useMemo(() => {
    const initialDoc = createInitialDocument(initialText, schema);
    return buildPMDocumentState({ events, schema, initialDoc });
  }, [events, initialText]);

  // Calculate clamped initial state from props (for deep-linking)
  const clampedInitialState = useMemo(() => {
    if (!initialNavigationState?.moment || keyMoments.length === 0) {
      return { momentIndex: 0, isDrilledDown: false, editIndex: 0 };
    }

    // Convert from 1-indexed to 0-indexed and clamp to valid range
    const momentIndex = Math.max(
      0,
      Math.min(initialNavigationState.moment - 1, keyMoments.length - 1)
    );

    // Handle drill-down state
    if (initialNavigationState.edit !== undefined && initialNavigationState.edit !== null) {
      const maxEdit = keyMoments[momentIndex]?.events.length ?? 1;
      const editIndex = Math.max(
        0,
        Math.min(initialNavigationState.edit - 1, maxEdit - 1)
      );
      return { momentIndex, isDrilledDown: true, editIndex };
    }

    return { momentIndex, isDrilledDown: false, editIndex: 0 };
  }, [initialNavigationState, keyMoments]);

  // Navigation state - initialized from deep-link params if provided
  const [currentMomentIndex, setCurrentMomentIndex] = useState(
    clampedInitialState.momentIndex
  );
  const [isDrilledDown, setIsDrilledDown] = useState(
    clampedInitialState.isDrilledDown
  );
  const [editIndexInMoment, setEditIndexInMoment] = useState(
    clampedInitialState.editIndex
  );

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState<PlaybackSpeed>(1);
  const playbackIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Refs for focus management
  const progressBarRef = useRef<HTMLDivElement>(null);
  const breadcrumbRef = useRef<HTMLElement>(null);

  // Get current moment (safely handle empty moments)
  const currentMoment = keyMoments[currentMomentIndex] ?? null;

  // Calculate event indices for document state
  const { startIndex, endIndex, currentIndex } = useMemo(() => {
    if (!currentMoment) return { startIndex: -1, endIndex: -1, currentIndex: -1 };

    // Find the global index of the first event in the current moment
    let momentStartIndex = 0;
    for (let i = 0; i < currentMomentIndex; i++) {
      momentStartIndex += keyMoments[i].events.length;
    }

    const momentEndIndex = momentStartIndex + currentMoment.events.length - 1;

    if (isDrilledDown) {
      // Drilled down: return the specific edit index within the moment
      return {
        startIndex: momentStartIndex,
        endIndex: momentEndIndex,
        currentIndex: momentStartIndex + editIndexInMoment,
      };
    } else {
      // Not drilled down: return the full moment range
      return {
        startIndex: momentStartIndex,
        endIndex: momentEndIndex,
        currentIndex: momentEndIndex,
      };
    }
  }, [
    currentMomentIndex,
    editIndexInMoment,
    isDrilledDown,
    currentMoment,
    keyMoments,
  ]);

  // Get document state at current position
  // When drilled down, show single-event diff
  // When not drilled down, show combined diff for the entire moment
  const documentState = useMemo(() => {
    if (isDrilledDown) {
      return documentBuilder.getStateAtIndex(currentIndex);
    } else {
      const eventType = currentMoment?.type === "paste" ? "paste" : "insert";
      return documentBuilder.getStateForRange(startIndex, endIndex, eventType);
    }
  }, [
    documentBuilder,
    isDrilledDown,
    currentIndex,
    startIndex,
    endIndex,
    currentMoment?.type,
  ]);

  // Clear playback interval when component unmounts or playing stops
  useEffect(() => {
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, []);

  // Playback logic - advance to next position automatically
  useEffect(() => {
    if (!isPlaying) {
      // Clear interval when paused
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
      return;
    }

    // Start playback interval
    const delayMs = SPEED_CONFIG[speed].delayMs;
    playbackIntervalRef.current = setInterval(() => {
      if (isDrilledDown) {
        // Drilled down: advance through edits in current moment
        if (
          currentMoment &&
          editIndexInMoment < currentMoment.events.length - 1
        ) {
          setEditIndexInMoment((prev) => prev + 1);
        } else {
          // Reached end of moment - pop up to top level
          // Don't advance to next moment yet - let user see completed moment first
          // The next interval tick will handle advancing at the top level
          setIsDrilledDown(false);
          setEditIndexInMoment(0);
        }
      } else {
        // Top level: advance through moments
        if (currentMomentIndex < keyMoments.length - 1) {
          setCurrentMomentIndex((prev) => prev + 1);
        } else {
          // Reached end of replay
          setIsPlaying(false);
        }
      }
    }, delayMs);

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
    };
  }, [
    isPlaying,
    speed,
    isDrilledDown,
    editIndexInMoment,
    currentMomentIndex,
    keyMoments,
    currentMoment,
  ]);

  // Track previous isPlaying state to detect pause events
  const wasPlayingRef = useRef(false);

  // Notify parent of navigation changes (for URL sync)
  // Only fires when NOT playing (manual navigation) or when transitioning to paused
  useEffect(() => {
    if (!onNavigationChange) return;

    // Check if we just paused (was playing, now not playing)
    const justPaused = wasPlayingRef.current && !isPlaying;
    wasPlayingRef.current = isPlaying;

    // Update URL on pause or when not playing (manual navigation)
    if (!isPlaying || justPaused) {
      onNavigationChange({
        moment: currentMomentIndex + 1, // Convert to 1-indexed
        edit: isDrilledDown ? editIndexInMoment + 1 : null, // Convert to 1-indexed
      });
    }
  }, [
    currentMomentIndex,
    isDrilledDown,
    editIndexInMoment,
    isPlaying,
    onNavigationChange,
  ]);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    if (isDrilledDown) {
      // Drilled down: go to previous edit
      if (editIndexInMoment > 0) {
        setEditIndexInMoment((prev) => prev - 1);
      } else {
        // At start of moment - pop up to top level
        setIsDrilledDown(false);
        setEditIndexInMoment(0);
      }
    } else {
      // Top level: go to previous moment
      if (currentMomentIndex > 0) {
        setCurrentMomentIndex((prev) => prev - 1);
      }
    }
  }, [isDrilledDown, editIndexInMoment, currentMomentIndex]);

  const handleNext = useCallback(() => {
    if (isDrilledDown) {
      // Drilled down: go to next edit
      if (
        currentMoment &&
        editIndexInMoment < currentMoment.events.length - 1
      ) {
        setEditIndexInMoment((prev) => prev + 1);
      } else {
        // At end of moment - pop up to top level
        setIsDrilledDown(false);
        setEditIndexInMoment(0);
        // Advance to next moment if available
        if (currentMomentIndex < keyMoments.length - 1) {
          setCurrentMomentIndex((prev) => prev + 1);
        }
      }
    } else {
      // Top level: go to next moment
      if (currentMomentIndex < keyMoments.length - 1) {
        setCurrentMomentIndex((prev) => prev + 1);
      }
    }
  }, [
    isDrilledDown,
    editIndexInMoment,
    currentMomentIndex,
    keyMoments,
    currentMoment,
  ]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleDrillDown = useCallback(() => {
    setIsDrilledDown(true);
    setEditIndexInMoment(0);
    // Pause playback when drilling down
    setIsPlaying(false);
    // Move focus to breadcrumb after drill-down
    requestAnimationFrame(() => {
      const button = breadcrumbRef.current?.querySelector("button");
      if (button instanceof HTMLElement) {
        button.focus();
      }
    });
  }, []);

  const handlePopUp = useCallback(() => {
    setIsDrilledDown(false);
    setEditIndexInMoment(0);
    // Move focus to progress bar after pop-up
    requestAnimationFrame(() => {
      const focusable = progressBarRef.current?.querySelector('[tabindex="0"]');
      if (focusable instanceof HTMLElement) {
        focusable.focus();
      }
    });
  }, []);

  const handleSegmentClick = useCallback(
    (index: number) => {
      if (isDrilledDown) {
        // Drilled down: clicking segment navigates to that edit
        setEditIndexInMoment(index);
      } else {
        // Top level: clicking segment navigates to that moment
        setCurrentMomentIndex(index);
      }
    },
    [isDrilledDown]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture if focus is in an input/textarea/select
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Don't capture arrow keys when focus is on progress bar segments
      // (they handle their own keyboard navigation)
      const target = e.target as HTMLElement;
      if (
        (e.key === "ArrowLeft" || e.key === "ArrowRight") &&
        target.closest('[role="group"]')
      ) {
        return;
      }

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          if (isDrilledDown) {
            handlePopUp();
          }
          break;
        case " ":
          e.preventDefault();
          handlePlayPause();
          break;
        case "ArrowLeft":
          e.preventDefault();
          handlePrevious();
          break;
        case "ArrowRight":
          e.preventDefault();
          handleNext();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isDrilledDown, handlePopUp, handlePlayPause, handlePrevious, handleNext]);

  // Calculate navigation constraints
  const canGoPrevious = isDrilledDown
    ? editIndexInMoment > 0
    : currentMomentIndex > 0;
  const canGoNext = isDrilledDown
    ? currentMoment
      ? editIndexInMoment < currentMoment.events.length - 1
      : false
    : currentMomentIndex < keyMoments.length - 1;

  // Progress bar props
  const progressTotal = isDrilledDown
    ? currentMoment?.events.length ?? 0
    : keyMoments.length;
  const progressCurrent = isDrilledDown
    ? editIndexInMoment
    : currentMomentIndex;
  const progressLabel = isDrilledDown
    ? strings.highlightPosition(
        editIndexInMoment + 1,
        currentMoment?.events.length ?? 0
      )
    : strings.momentProgress(currentMomentIndex + 1, keyMoments.length);

  // Handle edge cases: no events or no moments
  if (events.length === 0 || keyMoments.length === 0) {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        <div className="rounded-lg border border-border bg-surface-secondary p-8 text-center">
          <p className="text-text-theme-secondary">
            {strings.noEventsAvailable}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col gap-4 ${className}`}
      data-testid="replay-container"
    >
      {/* Breadcrumb (only when drilled down) */}
      {isDrilledDown && currentMoment && (
        <nav ref={breadcrumbRef} className="flex-shrink-0">
          <ReplayBreadcrumb
            currentMoment={currentMoment}
            momentIndex={currentMomentIndex + 1}
            onPopUp={handlePopUp}
          />
        </nav>
      )}

      {/* Progress Bar */}
      <div ref={progressBarRef} className="flex-shrink-0">
        <ReplayProgressBar
          total={progressTotal}
          current={progressCurrent}
          onSegmentClick={handleSegmentClick}
          label={progressLabel}
        />
      </div>

      {/* Document View - Rich Text ProseMirror (flexible, scrollable) */}
      <ReplayViewProseMirror
        document={documentState.doc}
        changeRanges={documentState.changeRanges}
        autoScroll={true}
        emptyMessage={strings.noContentYet}
      />

      {/* Moment Card */}
      {currentMoment && (
        <div className="flex-shrink-0">
          <ReplayMomentCard
            moment={currentMoment}
            isDrilledDown={isDrilledDown}
            editIndexInMoment={editIndexInMoment}
            onDrillDown={handleDrillDown}
          />
        </div>
      )}

      {/* Playback Controls */}
      <div className="flex-shrink-0">
        <ReplayControls
          isPlaying={isPlaying}
          onPlayPause={handlePlayPause}
          onPrevious={handlePrevious}
          onNext={handleNext}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          speed={speed}
          onSpeedChange={setSpeed}
          SelectComponent={SelectComponent}
        />
      </div>
    </div>
  );
}

export default ReplayContainer;
